import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useAddNewOrderEnabled -- shared read of the SuperAdmin
 * ``add_new_order_enabled`` platform switch (card #645, Jake 2026-10-29).
 *
 * The distributor Orders page "Add New Order" button, and the page it opens,
 * are closed until an SA turns this on. Default OFF.
 *
 * Fail-CLOSED FALSE: ``addNewOrderEnabled`` reads FALSE while the fetch is in
 * flight, if it errors, and if the payload is not a real boolean. A failed
 * read must never reveal an entry point an SA has not enabled.
 *
 * Two readers, one value:
 *   - ``useAddNewOrderEnabled()``: one GET per page load (module-level cache +
 *     in-flight dedupe). Drives what the Orders page SHOWS.
 *   - ``refreshAddNewOrderEnabled()``: always asks the server and resolves to
 *     the answer. The route guard uses it so the decision is made on a loaded,
 *     current value, not on whatever the page cached earlier.
 *
 *   const { addNewOrderEnabled, loading, error } = useAddNewOrderEnabled()
 */
const ENDPOINT = '/api/platform-settings/add-new-order-enabled'

const _value = ref(false)
const _loading = ref(false)
const _error = ref(null)
let _inflight = null
let _resolved = false

function _read() {
    _loading.value = true
    _error.value = null
    // The leading .then also turns a synchronous throw from the client into
    // the same fail-closed path as a rejected request.
    return Promise.resolve()
        .then(() => axiosInstance.get(ENDPOINT))
        .then((resp) => {
            const raw = resp && resp.data ? resp.data.value : null
            // Only a real boolean true turns the entry points ON.
            _value.value = raw === true
            _resolved = true
            return _value.value
        })
        .catch((err) => {
            // Fail CLOSED; not marked resolved so a later caller retries.
            _error.value = err
            _value.value = false
            _resolved = false
            return false
        })
        .finally(() => {
            _loading.value = false
        })
}

function _fetchOnce() {
    if (_resolved) return Promise.resolve(_value.value)
    if (_inflight) return _inflight
    _inflight = _read().finally(() => {
        _inflight = null
    })
    return _inflight
}

/** Always read the server; resolves to the boolean answer (FALSE on failure). */
export function refreshAddNewOrderEnabled() {
    return _read()
}

/** Drop the cached answer so the next reader asks the server (SA card calls this after a save). */
export function invalidateAddNewOrderEnabled() {
    _resolved = false
}

/** Test-only: reset the module-level shared state. */
export function _resetAddNewOrderEnabledCache() {
    _value.value = false
    _loading.value = false
    _error.value = null
    _inflight = null
    _resolved = false
}

export function useAddNewOrderEnabled() {
    _fetchOnce()
    return {
        addNewOrderEnabled: _value,
        loading: _loading,
        error: _error,
    }
}

export default useAddNewOrderEnabled
